"use client";

import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { onConflict } from "../../lib/conflictBus";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog.jsx";

/**
 * Phase 5 — mounted once near the app root. Listens on the conflict bus and
 * surfaces a choice whenever a save comes back as a 409 conflict: keep the
 * change just made on this device, or drop it and pick up the version that
 * won on the server.
 */
export function ConflictResolutionDialog() {
  const [pending, setPending] = useState(null);

  useEffect(() => {
    return onConflict((details) => setPending(details));
  }, []);

  const resolve = (choice) => {
    if (!pending) return;
    pending.resolve(choice);
    setPending(null);
  };

  const serverUpdatedAt = pending?.serverRecord?.updatedAt
    ? new Date(pending.serverRecord.updatedAt).toLocaleString()
    : null;

  return (
    <Dialog
      open={!!pending}
      onOpenChange={(open) => !open && resolve("discard")}
    >
      <DialogContent showCloseButton={false} className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="size-5 text-amber-500"
              aria-hidden="true"
            />
            <DialogTitle>Someone else updated this record</DialogTitle>
          </div>
          <DialogDescription>
            {pending?.entityLabel || "This record"} was changed on another
            device
            {serverUpdatedAt ? ` at ${serverUpdatedAt}` : ""} while you were
            working offline. Choose which version to keep.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <button
            type="button"
            onClick={() => resolve("discard")}
            className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Use their version
          </button>
          <button
            type="button"
            onClick={() => resolve("overwrite")}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Keep my changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
